// ============================================================
// AVERAGED DAMAGE - the swing taken out of the damage roll.
//
// A table ruling rather than a per-character trait, because a GM who
// wants predictable damage usually wants it for the pace of the whole
// fight, not for one person.
//
// THERE ARE THREE MODES, NOT FOUR. "Enemy attacks only" cannot be built:
// the app has no notion of sides - no `ownerId`, no allegiance anywhere
// on `Combatant` - and `rules-spec.md` §15.4 says so deliberately
// ("the simulator does NOT distinguish between 'player' and 'enemy'").
// `protagonists` is the honest version of that intent: it targets the
// characters the GM has explicitly flagged.
// ============================================================
import type { DiceResult, DiceGroup } from '../../types';

export type AveragedDamageMode = 'off' | 'protagonists' | 'all';

export interface AveragedDamageChoice {
  key: AveragedDamageMode;
  label: string;
  hint?: string;
}

export const AVERAGED_DAMAGE_MODES: AveragedDamageChoice[] = [
  { key: 'off', label: 'Damage is rolled', hint: 'As written. The dice decide.' },
  {
    key: 'protagonists',
    label: 'Averaged against protagonists',
    hint: 'Damage aimed at a flagged character takes its average instead of rolling. Given their soak you can work out exactly how many rounds they have - and with wound bands on, nobody else can tell.',
  },
  {
    key: 'all',
    label: 'Averaged for everyone',
    hint: 'Every damage roll in the session takes its average. Quicker to resolve and far less swingy - but your players will notice at once that their bolter always does the same, so this is a ruling to announce rather than one to keep.',
  },
];

export const DEFAULT_AVERAGED_DAMAGE: AveragedDamageMode = 'off';

const KEYS = new Set(AVERAGED_DAMAGE_MODES.map((m) => m.key));

/** A stored ruling is untrusted input - a later build may have written a mode this one lacks. */
export function isAveragedDamageMode(v: unknown): v is AveragedDamageMode {
  return typeof v === 'string' && KEYS.has(v as AveragedDamageMode);
}

/**
 * Replace every rolled die with its average, keeping flat bonuses.
 *
 * `(sides + 1) / 2` rounded, so a d10 is 6 and a d5 is 3 - rounding up
 * on the half rather than down, which is the friendlier reading for the
 * attacker and the one the tables tend to assume.
 *
 * DELIBERATELY APPLIED AFTER THE ROLL rather than instead of it. The
 * dice have already been drawn by the time this runs, so the sequence
 * advances exactly as it would have - which keeps the E2E seeded-dice
 * harness (gotcha #5) honest. The wasted roll costs nothing.
 */
export function applyAverage(dr: DiceResult): DiceResult {
  const groups: DiceGroup[] = dr.results.map((g) => {
    if (g.type === 'flat' || !g.rolls || g.rolls.length === 0) return { ...g };
    const sides = g.sides || 10;
    const mean = Math.round((sides + 1) / 2);
    const rolls = g.rolls.map(() => mean);
    return { ...g, rolls, total: rolls.reduce((a, b) => a + b, 0) };
  });
  return {
    results: groups,
    total: groups.reduce((s, g) => s + (g.type === 'flat' ? g.value || 0 : g.total || 0), 0),
  };
}

/** Whether this hit's damage should be averaged. */
export function shouldAverage(mode: AveragedDamageMode, isProtagonist: boolean): boolean {
  return mode === 'all' || (mode === 'protagonists' && isProtagonist);
}
